import {
  parseBulkPermissionOperationSuccessResources,
  parsePermission,
  parsePermissions,
  parseV1Permission,
  parseV1Permissions
} from '../../utils/issuePermissions-helpers';
import lodashPartition from 'lodash/partition';
import {
  AdminPermissionBulkOperationStatus,
  NoPermissions,
  PermissionIdsToActions,
  PermissionsIdsToLevels,
  PermissionsResourceTypes
} from '../../constants/issues-admin';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';
import {HQ_USER_TYPE} from '../../constants/hq-types';
import {getActiveAdminUsersSortedByName} from '../selectors';
import {sortAlphabeticallyByPath} from '../../utils/sortingHelpers';

export const ISSUES_PERMISSIONS_FETCH_START = 'ISSUES_PERMISSIONS_FETCH_START';
export const ISSUES_PERMISSIONS_FETCH_API_FULFILLED = 'ISSUES_PERMISSIONS_FETCH_API_FULFILLED';
export const ISSUES_PERMISSIONS_FETCH_API_REJECTED = 'ISSUES_PERMISSIONS_FETCH_API_REJECTED';

export const ISSUES_PERMISSIONS_BULK_CREATE_START = 'ISSUES_PERMISSIONS_BULK_CREATE_START';
export const ISSUES_PERMISSIONS_BULK_CREATE_API_FULFILLED = 'ISSUES_PERMISSIONS_BULK_CREATE_API_FULFILLED';
export const ISSUES_PERMISSIONS_BULK_CREATE_API_REJECTED = 'ISSUES_PERMISSIONS_BULK_CREATE_API_REJECTED';

export const ISSUES_PERMISSIONS_UPDATE_START = 'ISSUES_PERMISSIONS_UPDATE_START';
export const ISSUES_PERMISSIONS_UPDATE_API_FULFILLED = 'ISSUES_PERMISSIONS_UPDATE_API_FULFILLED';
export const ISSUES_PERMISSIONS_UPDATE_API_REJECTED = 'ISSUES_PERMISSIONS_UPDATE_API_REJECTED';

export const ISSUES_PERMISSIONS_DELETE_START = 'ISSUES_PERMISSIONS_DELETE_START';
export const ISSUES_PERMISSIONS_DELETE_API_FULFILLED = 'ISSUES_PERMISSIONS_DELETE_API_FULFILLED';
export const ISSUES_PERMISSIONS_DELETE_API_REJECTED = 'ISSUES_PERMISSIONS_DELETE_API_REJECTED';

// We show admins in the permissions table first, unless there are rows added within the same session
// Because we want to show new lines even before admins, the ordering needs to be saved to the store rather than the component.
const mockAdminRow = admin => ({
  value: '',
  name: admin.name,
  subjectId: admin.oxygenId,
  subjectType: HQ_USER_TYPE,
  [PermissionsResourceTypes.ISSUES]: {
    id: admin.oxygenId,
    levels: [],
    subject: {
      id: admin.oxygenId,
      name: admin.name
    },
    subjectType: HQ_USER_TYPE,
    resourceType: PermissionsResourceTypes.ISSUES
  },
  isProjectAdmin: true
});

export const fetchIssuesPermissions = () => (dispatch, getState, {jsonApi, issuesV2Api}) => {
  dispatch({
    type: ISSUES_PERMISSIONS_FETCH_START
  });
  const activeAdminUsers = getActiveAdminUsersSortedByName(getState());
  const adminPermissions = activeAdminUsers.map(mockAdminRow);

  const usePermissionsV2 = featureFlags.get(FEATURE_FLAGS.PERMISSIONS_V2);
  (usePermissionsV2 ? issuesV2Api.getIssuesPermissions() : jsonApi.fetchIssuesPermissions())
    .then(result => (usePermissionsV2 ? parsePermissions(result, getState()) : parseV1Permissions(result, getState())))
    .then(parsedPermissions => {
      const nonAdminPermissions = sortAlphabeticallyByPath(parsedPermissions);
      dispatch({
        type: ISSUES_PERMISSIONS_FETCH_API_FULFILLED,
        payload: adminPermissions.concat(nonAdminPermissions)
      });
    });
};

const parsePermissionStringToLevels = permissionString => {
  if (!permissionString) {
    return [NoPermissions];
  }

  return permissionString.split('').map(permissionsId => ({
    name: PermissionsIdsToLevels[permissionsId]
  }));
};

const parsePermissionStringToActions = permissionString =>
  permissionString.split('').map(id => PermissionIdsToActions[id]);

export const updateIssuesPermissions = (permissionId, value) => (dispatch, getState, {jsonApi, issuesV2Api}) => {
  dispatch({
    type: ISSUES_PERMISSIONS_UPDATE_START
  });
  if (featureFlags.get(FEATURE_FLAGS.PERMISSIONS_V2)) {
    issuesV2Api
      .updateIssuesPermissions(permissionId, {actions: parsePermissionStringToActions(value)})
      .then(response => {
        dispatch({
          type: ISSUES_PERMISSIONS_UPDATE_API_FULFILLED,
          payload: parsePermission(response, getState())
        });
      })
      .catch(err => {
        dispatch({
          type: ISSUES_PERMISSIONS_UPDATE_API_REJECTED,
          payload: err
        });
      });
  } else {
    jsonApi
      .updateIssuesPermissions(permissionId, {levels: parsePermissionStringToLevels(value)})
      .then(response => {
        dispatch({
          type: ISSUES_PERMISSIONS_UPDATE_API_FULFILLED,
          payload: parseV1Permission(response, getState())
        });
      })
      .catch(err => {
        dispatch({
          type: ISSUES_PERMISSIONS_UPDATE_API_REJECTED,
          payload: err
        });
      });
  }
};

export const deleteIssuesPermissionsEntry = (permissionId, onSuccess, onError) => (
  dispatch,
  getState,
  {jsonApi, issuesV2Api}
) => {
  dispatch({
    type: ISSUES_PERMISSIONS_DELETE_START
  });
  (featureFlags.get(FEATURE_FLAGS.PERMISSIONS_V2)
    ? issuesV2Api.deleteIssuesPermissions(permissionId)
    : jsonApi.deleteIssuesPermissions(permissionId)
  ).then(
    () => {
      onSuccess && onSuccess();
      dispatch({
        type: ISSUES_PERMISSIONS_DELETE_API_FULFILLED,
        meta: permissionId
      });
    },
    err => {
      onError && onError();
      dispatch({
        ISSUES_PERMISSIONS_DELETE_API_REJECTED,
        payload: err
      });
    }
  );
};

export const createBulkIssuesPermissions = (permissionsData, onSuccess, onError) => (
  dispatch,
  getState,
  {issuesV2Api}
) => {
  dispatch({
    type: ISSUES_PERMISSIONS_BULK_CREATE_START
  });
  issuesV2Api.createBulkIssuesPermissions(permissionsData).then(
    response => {
      const [successOperations, failedOperations] = lodashPartition(
        response.operations,
        operation => operation.status === AdminPermissionBulkOperationStatus.SUCCESS
      );
      const successPermissions = parseBulkPermissionOperationSuccessResources(
        successOperations.map(op => op.resource),
        getState()
      );
      const failedPermissions = failedOperations.map(op => op.resource);
      onSuccess && onSuccess({successPermissions, failedPermissions});
      dispatch({
        type: ISSUES_PERMISSIONS_BULK_CREATE_API_FULFILLED,
        payload: {successPermissions}
      });
    },
    err => {
      onError && onError();
      dispatch({
        type: ISSUES_PERMISSIONS_BULK_CREATE_API_REJECTED,
        payload: err
      });
    }
  );
};
